// Hook to fetch and cache users for owner display and assignment
import { useQuery } from '@tanstack/react-query'
import { apiRequest } from '../lib/api'
import type { AssignableUser } from '../../shared/types'

interface UsersResponse {
  data: AssignableUser[]
}

// Fetch assignable users (filtered by current user's permissions)
export function useAssignableUsers() {
  return useQuery({
    queryKey: ['users', 'assignable'],
    queryFn: () => apiRequest<UsersResponse>('/users/assignable'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// Hook that provides a lookup map and helper function
export function useUserLookup() {
  const { data, isLoading } = useAssignableUsers()

  const usersMap = new Map<string, AssignableUser>()
  if (data?.data) {
    for (const user of data.data) {
      usersMap.set(user.id, user)
    }
  }

  const getUser = (userId: string | null | undefined): AssignableUser | null => {
    if (!userId) return null
    return usersMap.get(userId) || null
  }

  const getUserName = (userId: string | null | undefined): string => {
    const user = getUser(userId)
    if (!user) return '-'
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`
    }
    return user.email
  }

  const getUserInitials = (userId: string | null | undefined): string => {
    const user = getUser(userId)
    if (!user) return '?'
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    }
    return user.email[0].toUpperCase()
  }

  return {
    users: data?.data || [],
    usersMap,
    getUser,
    getUserName,
    getUserInitials,
    isLoading,
  }
}
